import { getLocationsForPoint } from './geo-matching';
import { getLocationsForBeacon } from './beacon-matching';

/**
 * Pure helpers for the BFS Arrivals Audit Report.
 *
 * Every task doc on the flight is one "leg" (the original task, a ramp task and each
 * `_TRANSFERRED_n` copy are separate docs). A passenger's audit row is built from every
 * leg that carries that passenger, and GPS/beacon pings are kept strictly inside each
 * leg's allocated → finished window.
 */

export const AUDIT_TIME_ZONE = 'Europe/London';

// Pre-advised = booked at least 36h (less the same 5 min buffer the backend uses) before STA
const PRE_ADVISED_MINUTES = 36 * 60 - 5;

// Tasks for the same flight number on the neighbouring UTC date belong to another rotation
const MAX_TASK_TO_STA_OFFSET_MS = 12 * 60 * 60 * 1000;

// Unfinished legs are not queried past this, so a task left open doesn't pull a whole day of pings
const MAX_OPEN_LEG_MS = 6 * 60 * 60 * 1000;

//#region time
/** Millis from a Firestore Timestamp (seconds/_seconds), Date, epoch number or ISO string. */
export const toMillis = (ts) => {
    if (ts == null || ts === '') return null;
    if (typeof ts === 'number') return Number.isFinite(ts) ? ts : null;
    if (ts instanceof Date) return Number.isFinite(ts.getTime()) ? ts.getTime() : null;
    if (typeof ts === 'object') {
        const seconds = ts.seconds ?? ts._seconds;
        const nanoseconds = ts.nanoseconds ?? ts._nanoseconds ?? 0;
        if (typeof seconds === 'number') return seconds * 1000 + nanoseconds / 1e6;
        return null;
    }
    const ms = new Date(ts).getTime();
    return Number.isFinite(ms) ? ms : null;
};

const timeFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: AUDIT_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: AUDIT_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
});

/** "HH:mm" in Belfast local time, or "" when missing. */
export const formatAuditTime = (ms) => (ms == null ? '' : timeFormatter.format(new Date(ms)));

/** "DD/MM/YYYY" in Belfast local time, or "" when missing. */
export const formatAuditDate = (ms) => (ms == null ? '' : dateFormatter.format(new Date(ms)));
//#endregion

//#region flight
/** Time from the BFS AIDX operatingTimes array, e.g. ('SCT', 'ONB'). */
const getBfsOperatingTime = (times, timeType, qualifier) => {
    if (!Array.isArray(times)) return null;
    const entry = times.find(
        (t) => t?.attributes?.TimeType === timeType && (!qualifier || t?.attributes?.OperationQualifier === qualifier)
    );
    return entry?.$value ?? null;
};

/** Scheduled arrival, same source order as prm-cpu getScheduledTimeFromFlight. */
export const getScheduledArrivalMs = (flight) => {
    if (!flight) return null;
    const ot = flight.operatingTimes;
    const candidates = [
        flight.originalFlightTime,
        !Array.isArray(ot) ? ot?.ScheduledDateTime : null,
        flight.scheduled,
        getBfsOperatingTime(ot, 'SCT', 'ONB'),
        getBfsOperatingTime(ot, 'SCT'),
    ];
    for (const c of candidates) {
        const ms = toMillis(c);
        if (ms != null) return ms;
    }
    return null;
};

/** Actual arrival: on blocks, else touchdown. Same order as prm-cpu getActualArrivalTimeFromFlight. */
export const getActualArrivalMs = (flight) => {
    if (!flight) return null;
    const ot = flight.operatingTimes;
    if (Array.isArray(ot)) {
        return toMillis(getBfsOperatingTime(ot, 'ACT', 'ONB') || getBfsOperatingTime(ot, 'ACT', 'TDN'));
    }
    if (ot && typeof ot === 'object') {
        return toMillis(ot.ActualOnBlocksDateTime || ot.WheelsDownDateTime);
    }
    if (flight.operationInfo?.status === 'Actual') return toMillis(flight.operationInfo.time);
    return null;
};

/** UTC dates to query tasks on: the flight date, plus the STA's UTC date when a midnight arrival makes them differ. */
export const getTaskQueryDates = (flightDate, staMs) => {
    const dates = [flightDate].filter(Boolean);
    if (staMs != null) {
        const utcDate = new Date(staMs).toISOString().slice(0, 10);
        if (!dates.includes(utcDate)) dates.push(utcDate);
    }
    return dates;
};
//#endregion

//#region tasks
const isMarshallTask = (task) =>
    !!task?.combinedMarshallBooking || String(task?._id || '').includes('-marshall-task');

const isRampTask = (task) =>
    !!task?.rampBooking || !!task?.combinedRampBooking || String(task?._id || '').includes('-ramp-task');

/**
 * Keeps the passenger tasks for this arrival: right airport and flight number, not cancelled,
 * no marshall/break tasks, and (when both are known) within 12h of STA so a same-number
 * rotation on the neighbouring date is excluded. Deduped by _id across the date queries.
 */
export const selectArrivalTasks = (tasks, { iata, flightNumber, staMs }) => {
    const seen = new Set();
    return (Array.isArray(tasks) ? tasks : []).filter((task) => {
        if (!task?._id || seen.has(task._id)) return false;
        seen.add(task._id);
        const info = task.flightInformation || {};
        if (info.arrivalAirport !== iata) return false;
        if (flightNumber && info.flightNumber !== flightNumber) return false;
        if (task.cancelled || task.isBreak || task.breakTask || isMarshallTask(task)) return false;
        const taskFlightMs = toMillis(info.flightTime);
        if (staMs != null && taskFlightMs != null && Math.abs(taskFlightMs - staMs) > MAX_TASK_TO_STA_OFFSET_MS) {
            return false;
        }
        return true;
    });
};

export const getLegType = (task) => {
    if (task?.isTransferred) return 'Transfer';
    if (isRampTask(task)) return 'Ramp';
    if (task?.isALTPickUp) return 'ALT pick-up';
    return 'Arrival';
};

/** Passengers on a task: passengers[] for grouped/self-allocated tasks, else the single-pax fields. */
const getTaskPassengers = (task) => {
    if (Array.isArray(task?.passengers) && task.passengers.length) {
        return task.passengers.map((p) => ({
            bookingId: p?.originalBookingId || null,
            name: p?.name || '',
            assistance: p?.assistance,
            bookingCreateDate: p?.bookingCreateDate,
        }));
    }
    const info = task?.passengerInformation || {};
    if (!info.title && !task?.originalBookingId) return [];
    return [{
        bookingId: task?.originalBookingId || null,
        name: info.title || '',
        assistance: info.assistance,
        bookingCreateDate: task?.bookingCreateDate,
    }];
};

const toAssistanceList = (assistance) => {
    if (Array.isArray(assistance)) return assistance.filter(Boolean).map(String);
    if (typeof assistance === 'string' && assistance.trim()) return [assistance.trim()];
    return [];
};

/**
 * "Pre-advised" / "Non-advised" from the booking create time vs STA.
 * Approximation of prm-cpu isPreAdvisedArrivalBooking: the task copy of the booking carries no
 * movementReportOveride or assistance-upgrade history, so those overrides are not applied.
 */
export const getAdviceStatus = (bookingCreateDate, staMs) => {
    const createdMs = toMillis(bookingCreateDate);
    if (createdMs == null || staMs == null) return '';
    return (staMs - createdMs) / 60000 >= PRE_ADVISED_MINUTES ? 'Pre-advised' : 'Non-advised';
};

/** Ping window for a leg: allocated → finished. Open legs run to now, capped at 6h after allocation. */
export const getLegWindow = (leg, nowMs = Date.now()) => {
    const start = leg.allocated ?? leg.confirm ?? leg.atLocation ?? leg.start;
    if (start == null) return null;
    const end = leg.finish ?? Math.min(nowMs, start + MAX_OPEN_LEG_MS);
    if (end < start) return null;
    return { start, end, open: leg.finish == null };
};

const buildLeg = (task, nowMs) => {
    const leg = {
        taskId: task._id,
        employeeId: task.employeeId || null,
        employeeName: task.employeeName && task.employeeName !== 'N/A' ? task.employeeName : (task.employeeId || 'Unassigned'),
        type: getLegType(task),
        allocated: toMillis(task.allocatedTime),
        confirm: toMillis(task.acknowledgedTime),
        atLocation: toMillis(task.atLocationTime),
        start: toMillis(task.startTime),
        finish: toMillis(task.completeTime),
        // Legacy in-place transfers swap employeeId and only leave this history behind
        transferHistory: (Array.isArray(task.taskTransferHistory) ? task.taskTransferHistory : []).map((h) => ({
            fromName: h?.transferredByName || '',
            toName: h?.transferredToEmployeeName || '',
            at: toMillis(h?.transferredAt),
        })),
    };
    leg.window = getLegWindow(leg, nowMs);
    return leg;
};

const minOf = (values) => {
    const v = values.filter((x) => x != null);
    return v.length ? Math.min(...v) : null;
};

const maxOf = (values) => {
    const v = values.filter((x) => x != null);
    return v.length ? Math.max(...v) : null;
};

/**
 * One audit row per passenger, each with every leg (task doc) that carried them, in time order.
 * Confirm/At location/Start are the earliest across legs; Finish is the last leg's finish,
 * and only when every leg has finished.
 */
export const buildPassengerAudits = (tasks, { staMs, nowMs = Date.now() } = {}) => {
    const byPax = new Map();

    for (const task of tasks) {
        const leg = buildLeg(task, nowMs);
        for (const pax of getTaskPassengers(task)) {
            const key = pax.bookingId || `name:${pax.name.trim().toLowerCase()}`;
            if (!byPax.has(key)) {
                byPax.set(key, {
                    key,
                    bookingId: pax.bookingId,
                    name: pax.name,
                    assistance: [],
                    bookingCreateDate: pax.bookingCreateDate,
                    legs: [],
                });
            }
            const entry = byPax.get(key);
            if (!entry.name && pax.name) entry.name = pax.name;
            if (!entry.bookingCreateDate && pax.bookingCreateDate) entry.bookingCreateDate = pax.bookingCreateDate;
            for (const code of toAssistanceList(pax.assistance)) {
                if (!entry.assistance.includes(code)) entry.assistance.push(code);
            }
            if (!entry.legs.some((l) => l.taskId === leg.taskId)) entry.legs.push(leg);
        }
    }

    const rows = Array.from(byPax.values()).map((entry) => {
        const legs = entry.legs.sort(
            (a, b) => (a.allocated ?? a.confirm ?? Infinity) - (b.allocated ?? b.confirm ?? Infinity)
        );
        const agents = [];
        for (const leg of legs) {
            for (const h of leg.transferHistory) {
                if (h.fromName && !agents.includes(h.fromName)) agents.push(h.fromName);
            }
            if (!agents.includes(leg.employeeName)) agents.push(leg.employeeName);
        }
        const allFinished = legs.length > 0 && legs.every((l) => l.finish != null);
        return {
            ...entry,
            name: entry.name || 'Unknown passenger',
            legs,
            agents,
            adviceStatus: getAdviceStatus(entry.bookingCreateDate, staMs),
            confirm: minOf(legs.map((l) => l.confirm)),
            atLocation: minOf(legs.map((l) => l.atLocation)),
            start: minOf(legs.map((l) => l.start)),
            finish: allFinished ? maxOf(legs.map((l) => l.finish)) : null,
        };
    });

    return rows.sort((a, b) => (a.confirm ?? a.legs[0]?.allocated ?? Infinity) - (b.confirm ?? b.legs[0]?.allocated ?? Infinity)
        || a.name.localeCompare(b.name));
};

/** "Ramp – Ian Armstrong 15:01–15:09 → Transfer – Vika Zak 15:09–15:15" */
export const describeJourney = (legs) =>
    legs
        .map((l) => `${l.type} – ${l.employeeName} ${formatAuditTime(l.allocated ?? l.confirm) || '?'}–${formatAuditTime(l.finish) || 'open'}`)
        .join(' → ');

/** Unique (employee, window) pairs to fetch pings for, so shared legs are fetched once. */
export const getPingRequests = (audits) => {
    const byKey = new Map();
    for (const audit of audits) {
        for (const leg of audit.legs) {
            if (!leg.employeeId || !leg.window) continue;
            const key = getPingKey(leg);
            if (!byKey.has(key)) byKey.set(key, { key, employeeId: leg.employeeId, ...leg.window });
        }
    }
    return Array.from(byKey.values());
};

export const getPingKey = (leg) => `${leg.employeeId}|${leg.window?.start}|${leg.window?.end}`;
//#endregion

//#region pings
/**
 * GPS + beacon pings for one leg, strictly within its window, resolved to named airport locations.
 * Beacon points are placed at the centre of the matched location's geofence so they can be mapped.
 */
export const buildLegPings = ({ geolocations = [], beaconEvents = [] }, window, locations = []) => {
    if (!window) return [];
    const inWindow = (ms) => ms != null && ms >= window.start && ms <= window.end;
    const rows = [];

    for (const g of geolocations) {
        const ms = toMillis(g?.timestamp);
        const lat = Number(g?.latitude);
        const lng = Number(g?.longitude);
        if (!inWindow(ms) || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        rows.push({
            ms,
            source: 'GPS',
            lat,
            lng,
            accuracy: Number.isFinite(Number(g?.accuracy)) ? Math.round(Number(g.accuracy)) : null,
            locations: getLocationsForPoint(locations, lat, lng),
        });
    }

    for (const b of beaconEvents) {
        const ms = toMillis(b?.timestamp);
        if (!inWindow(ms)) continue;
        const matches = getLocationsForBeacon(locations, b?.major, b?.minor);
        const anchor = getBeaconAnchor(locations, b?.major, b?.minor);
        rows.push({
            ms,
            source: 'Beacon',
            lat: anchor?.[0] ?? null,
            lng: anchor?.[1] ?? null,
            major: b?.major,
            minor: b?.minor,
            rssi: b?.rssi ?? null,
            locations: matches,
        });
    }

    return rows.sort((a, b) => a.ms - b.ms);
};

const getBeaconAnchor = (locations, major, minor) => {
    const loc = (locations || []).find(
        (l) =>
            Array.isArray(l?.beaconDetails) &&
            l.beaconDetails.some((d) => Number(d.major) === Number(major) && Number(d.minor) === Number(minor))
    );
    const g = loc?.geofenceDetails;
    if (!g?.type) return null;
    const [lat, lng] = g.type === 'circular'
        ? [Number(g.circular?.lat), Number(g.circular?.long)]
        : [Number(g.polygon?.[0]?.[0]), Number(g.polygon?.[0]?.[1])];
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
};

const locationLabel = (ping) => {
    if (ping.locations.length) return ping.locations.join(', ');
    if (ping.source === 'Beacon') return `Unmapped beacon ${ping.major}-${ping.minor}`;
    return 'No mapped area';
};

/**
 * Condenses pings into readable rows: one per run of consecutive pings reporting the same
 * locations, e.g. { from: 15:00, to: 15:05, sources: 'GPS + Beacon', location: 'S22, S23' }.
 */
export const summarisePings = (pings) => {
    const out = [];
    for (const p of pings) {
        const location = locationLabel(p);
        const last = out[out.length - 1];
        if (last && last.location === location) {
            last.to = p.ms;
            last.count += 1;
            last.sourceSet.add(p.source);
            continue;
        }
        out.push({ from: p.ms, to: p.ms, location, count: 1, sourceSet: new Set([p.source]) });
    }
    return out.map(({ sourceSet, ...row }) => ({
        ...row,
        sources: ['GPS', 'Beacon'].filter((s) => sourceSet.has(s)).join(' + '),
    }));
};

/** "15:00–15:05", or "15:00" for a single minute. */
export const formatPingRange = (row) => {
    const from = formatAuditTime(row.from);
    const to = formatAuditTime(row.to);
    return from === to ? from : `${from}–${to}`;
};

/** "15:00–15:05  S22, S23 (GPS + Beacon)" */
export const formatPingSummaryLine = (row) => `${formatPingRange(row)}  ${row.location} (${row.sources})`;
//#endregion
